'use client';

import Chrysler from '@carvana/ds-icons/Chrysler';
import Jeep from '@carvana/ds-icons/Jeep';
import Ram from '@carvana/ds-icons/Ram';
import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import DodgeIcon from '~/icons/new-car/DodgeIcon';
import styles from './RotatingBrandLogo.module.css';

// DS icons hardcode their own path fill and ignore the color prop, so override it via CSS.
const FILL_OVERRIDE_CLASSES = '[&_path]:fill-[var(--icon-fill)]';
const fillOverrideStyle = {
  '--icon-fill': icon.inverse
} as React.CSSProperties;

const BRAND_LABELS = ['Chrysler', 'Dodge', 'Jeep', 'Ram'] as const;

const brandIcons: Record<string, typeof Chrysler> = {
  Chrysler,
  Jeep,
  Ram
};

const brandWidths: Record<string, string> = {
  Chrysler: 'w-[57px]',
  Dodge: 'w-[57px]',
  Jeep: 'w-[32px]',
  Ram: 'w-[32px]'
};

const ROTATION_INTERVAL_MS = 3000;

function BrandLogo({ label }: { label: string }) {
  const className = cx('h-full', brandWidths[label], FILL_OVERRIDE_CLASSES);

  if (label === 'Dodge') {
    return (
      <DodgeIcon
        arrowUsesFill
        aria-label="Dodge logo"
        className={className}
        color={icon.inverse}
        style={fillOverrideStyle}
      />
    );
  }

  const Icon = brandIcons[label];

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      aria-label={`${label} logo`}
      className={className}
      style={fillOverrideStyle}
    />
  );
}

export default function RotatingBrandLogo() {
  const prefersReducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const intervalId = setInterval(() => {
      setActiveIndex(previous => (previous + 1) % BRAND_LABELS.length);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [prefersReducedMotion]);

  const labels = prefersReducedMotion ? [BRAND_LABELS[0]] : BRAND_LABELS;

  return (
    <div
      aria-hidden="true"
      className="relative max-w-8xl h-4xl"
      data-testid="rotating-brand-logo"
    >
      {labels.map((label, index) => (
        <span
          key={label}
          className={styles.brandLogo}
          data-active={prefersReducedMotion || index === activeIndex}
          data-testid={`brand-logo-${label.toLowerCase()}`}
        >
          <BrandLogo label={label} />
        </span>
      ))}
    </div>
  );
}
